import {
  collection,
  query as firestoreQuery,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import {
  Build,
  BuildStatus,
  IntrospectBreakpointStatus,
  IntrospectShellStatus,
} from '@todesktop/shared';
import firestore from '../../../utilities/firestore';
import findAppUserId from '../../../utilities/findAppUserId';
import getProjectConfig from '../../../utilities/projectConfig/getProjectConfig';
import logger from '../../../utilities/logger';

export interface BuildWithIntrospection extends Build {
  introspect: NonNullable<Build['introspect']>;
}

export async function getRunningBuildsWithIntrospection(
  configPath?: string,
): Promise<BuildWithIntrospection[]> {
  const { id: appId } = getProjectConfig(configPath).config;
  const { id: userId } = await findAppUserId(appId);

  logger.debug({ appId, userId }, 'getRunningBuildsWithIntrospection');

  const buildsCollection = collection(
    firestore,
    `users/${userId}/applications/${appId}/builds`,
  );

  const q = firestoreQuery(
    buildsCollection,
    where('status', 'in', [BuildStatus.building, BuildStatus.preparation]),
    orderBy('createdAt', 'desc'),
    firestoreLimit(10),
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return [];
  }

  const builds = snapshot.docs
    .map((docSnapshot) => docSnapshot.data() as Build)
    .filter((build) => {
      if (!build.introspect) return false;

      const hasEnabledPlatform =
        build.introspect.mac?.enabled ||
        build.introspect.windows?.enabled ||
        build.introspect.linux?.enabled;

      return hasEnabledPlatform;
    }) as BuildWithIntrospection[];

  return builds;
}

export async function getLatestBuildWithIntrospection(
  configPath?: string,
): Promise<BuildWithIntrospection | null> {
  const { id: appId } = getProjectConfig(configPath).config;
  const { id: userId } = await findAppUserId(appId);

  logger.debug({ appId, userId }, 'getLatestBuildWithIntrospection');

  const buildsCollection = collection(
    firestore,
    `users/${userId}/applications/${appId}/builds`,
  );

  const q = firestoreQuery(
    buildsCollection,
    orderBy('createdAt', 'desc'),
    firestoreLimit(20),
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  for (const docSnapshot of snapshot.docs) {
    const build = docSnapshot.data() as Build;

    if (build.introspect) {
      const hasEnabledPlatform =
        build.introspect.mac?.enabled ||
        build.introspect.windows?.enabled ||
        build.introspect.linux?.enabled;

      if (hasEnabledPlatform) {
        return build as BuildWithIntrospection;
      }
    }
  }

  return null;
}

export type PlatformInfo = {
  platform: 'mac' | 'windows' | 'linux';
  enabled: boolean;
  breakpointStatus: IntrospectBreakpointStatus;
  shellStatus: IntrospectShellStatus;
  displayName: string;
};

export async function getAvailablePlatforms(
  buildId: string,
  configPath?: string,
): Promise<PlatformInfo[]> {
  const { id: appId } = getProjectConfig(configPath).config;
  const { id: userId } = await findAppUserId(appId);

  logger.debug({ appId, userId, buildId }, 'getAvailablePlatforms');

  const buildRef = doc(
    firestore,
    `users/${userId}/applications/${appId}/builds/${buildId}`,
  );
  const buildSnapshot = await getDoc(buildRef);

  if (!buildSnapshot.exists()) {
    throw new Error(`Build ${buildId} not found`);
  }

  const build = buildSnapshot.data() as Build;

  if (!build.introspect) {
    throw new Error(
      `Build ${buildId} does not have introspection enabled. Please rebuild with --introspect`,
    );
  }

  const platforms: PlatformInfo[] = [];

  const platformMap: Array<['mac' | 'windows' | 'linux', string]> = [
    ['mac', 'macOS'],
    ['windows', 'Windows'],
    ['linux', 'Linux'],
  ];

  for (const [platform, displayName] of platformMap) {
    const platformData = build.introspect[platform];
    if (platformData?.enabled) {
      platforms.push({
        platform,
        enabled: platformData.enabled,
        breakpointStatus: platformData.breakpointStatus,
        shellStatus: platformData.shellStatus,
        displayName,
      });
    }
  }

  if (platforms.length === 0) {
    throw new Error(
      `No platforms with introspection enabled for build ${buildId}`,
    );
  }

  return platforms;
}
